import React from "react";
import { Server } from "lucide-react";

const inputCls = "w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20";
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const HField = ({ label, children }) => (
  <label className="flex flex-col gap-1.5 text-sm">
    <span className="text-xs font-medium text-zinc-600">{label}</span>
    {children}
  </label>
);

export const HostingSection = ({ project, meta, onChange, onLocalChange }) => {
  const tiers = (meta?.subscription_tiers || []).filter((t) => t.key !== "none");
  const enabled = project.subscription_tier && project.subscription_tier !== "none";
  const tierAmount = (key) => tiers.find((t) => t.key === key)?.amount || 0;

  const toggle = () => {
    if (enabled) onChange({ subscription_tier: "none", subscription_amount: 0 });
    else {
      const first = tiers[0]?.key || "essential";
      onChange({ subscription_tier: first, subscription_amount: tierAmount(first), billing_frequency: project.billing_frequency || "monthly", billing_mode: project.billing_mode || "manual" });
    }
  };

  return (
    <div data-testid="project-hosting-section" className="dt-card p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Server size={15} className="text-[#0066ff]" />
          <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Hosting &amp; Maintenance</h2>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          data-testid="project-hosting-toggle"
          onClick={toggle}
          className={`relative h-6 w-11 rounded-full transition-colors ${enabled ? "bg-[#0066ff]" : "bg-zinc-200"}`}
          title={enabled ? "Turn hosting off" : "Turn hosting on"}
        >
          <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>

      {!enabled ? (
        <p data-testid="project-hosting-off" className="mt-3 text-xs text-zinc-500">No hosting plan on this project. Turn it on to track a recurring plan.</p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <HField label="Plan tier">
            <select data-testid="project-hosting-tier" value={project.subscription_tier} onChange={(e) => onChange({ subscription_tier: e.target.value, subscription_amount: tierAmount(e.target.value) })} className={inputCls}>
              {tiers.map((t) => (<option key={t.key} value={t.key}>{t.label} · ${t.amount}/mo</option>))}
            </select>
          </HField>
          <HField label="Monthly amount ($)">
            <input
              data-testid="project-hosting-amount"
              type="number" min="0" step="0.01"
              value={project.subscription_amount ?? 0}
              onChange={(e) => onLocalChange({ subscription_amount: e.target.value })}
              onBlur={(e) => onChange({ subscription_amount: parseFloat(e.target.value || 0) || 0 })}
              className={inputCls}
            />
          </HField>
          <HField label="Payment frequency">
            <select data-testid="project-hosting-frequency" value={project.billing_frequency || "monthly"} onChange={(e) => onChange({ billing_frequency: e.target.value })} className={inputCls}>
              {(meta?.billing_frequencies || []).map((f) => (<option key={f} value={f}>{cap(f)}</option>))}
            </select>
          </HField>
          <HField label="Billing mode">
            <select data-testid="project-hosting-mode" value={project.billing_mode || "manual"} onChange={(e) => onChange({ billing_mode: e.target.value })} className={inputCls}>
              {(meta?.billing_modes || []).map((m) => (<option key={m} value={m}>{cap(m)}</option>))}
            </select>
          </HField>
          <HField label="Next billing date">
            <input data-testid="project-hosting-nextbill" type="date" value={project.next_billing_date || ""} onChange={(e) => onChange({ next_billing_date: e.target.value })} className={inputCls} />
          </HField>
        </div>
      )}
    </div>
  );
};
